'use client'

// Animated task board for the home page and the task-management pages. A scripted loop plays
// out a sprint: a teammate's cursor drags cards between stages, progress fills, comments get
// typed, new work arrives, and finished cards celebrate. Cards glide with a FLIP animation.
// Decorative only, so it is hidden from assistive tech and shows a still board when
// the visitor prefers reduced motion.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import t from './taskboard.module.css'

const COLS = [
  ['todo', 'To do', '#8a9bb0'],
  ['prog', 'In progress', '#0a6fd0'],
  ['review', 'In review', '#8b5cf6'],
  ['done', 'Done', '#16a36a'],
]

const PEOPLE = {
  A: ['Aarav', '#38a9ef'],
  R: ['Riya', '#f59e0b'],
  S: ['Sneha', '#16a36a'],
  M: ['Mohan', '#8b5cf6'],
  K: ['Kavya', '#ef4444'],
}

const INITIAL = [
  { id: 't1', title: 'Design homepage', tag: '#38a9ef', who: 'A', col: 'todo', p: 15, due: 'Fri', sub: '1/6', cm: 2, att: 3 },
  { id: 't2', title: 'Write release notes', tag: '#f59e0b', who: 'R', col: 'todo', p: 10, due: 'Mon', sub: '0/3', cm: 0, att: 1 },
  { id: 't3', title: 'Payroll integration', tag: '#16a36a', who: 'S', col: 'prog', p: 60, due: 'Today', tone: 'warn', sub: '3/5', cm: 5, att: 2 },
  { id: 't4', title: 'API rate limits', tag: '#0a6fd0', who: 'M', col: 'prog', p: 35, due: 'Wed', sub: '2/6', cm: 1, att: 0 },
  { id: 't5', title: 'Onboarding flow', tag: '#8b5cf6', who: 'M', col: 'review', p: 90, due: 'Tue', sub: '8/9', cm: 3, att: 4 },
  { id: 't6', title: 'QA checklist', tag: '#ef4444', who: 'K', col: 'done', p: 100, due: 'Done', tone: 'ok', sub: '5/5', cm: 2, att: 1 },
]
const NEW_TASK = { id: 't7', title: 'Client review', tag: '#ec4899', who: 'K', col: 'todo', p: 0, due: 'Thu', sub: '0/4', cm: 0, att: 0, fresh: true }

const ICON = {
  comment: <path d="M3 4.5h10v6.2H8.2L5 13v-2.3H3z" strokeLinejoin="round" />,
  clip: <path d="M11.2 7.4 7.4 11.2a2.2 2.2 0 1 1-3.1-3.1l4.2-4.2a1.5 1.5 0 0 1 2.1 2.1L6.6 9.9" strokeLinecap="round" strokeLinejoin="round" />,
  list: <path d="M6 4.5h7M6 8h7M6 11.5h7M3 4.5h.01M3 8h.01M3 11.5h.01" strokeLinecap="round" />,
  clock: <path d="M8 3.2a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6zM8 5.6V8l1.8 1.1" strokeLinecap="round" strokeLinejoin="round" />,
  bell: <path d="M4.5 10.5V7a3.5 3.5 0 0 1 7 0v3.5l1 1.2h-9zM6.8 13.2a1.3 1.3 0 0 0 2.4 0" strokeLinecap="round" strokeLinejoin="round" />,
  plus: <path d="M8 3.5v9M3.5 8h9" strokeLinecap="round" />,
  move: <path d="M3 8h10M9.5 4.5 13 8l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />,
  check: <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />,
}
const Ic = ({ n, size = 13 }) => (
  <svg viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    {ICON[n]}
  </svg>
)

const Av = ({ k, size = 22, pulse }) => (
  <i className={`${t.av} ${pulse ? t.avPulse : ''}`} style={{ background: PEOPLE[k][1], width: size, height: size, fontSize: size * 0.46 }}>
    {k}
  </i>
)

// Eases a number toward its target so the progress readout counts instead of jumping.
function useCount(target) {
  const [v, setV] = useState(target)
  const from = useRef(target)
  useEffect(() => {
    const start = performance.now()
    const a = from.current
    let raf
    const tick = (now) => {
      const k = Math.min(1, (now - start) / 900)
      const e = 1 - Math.pow(1 - k, 3)
      setV(Math.round(a + (target - a) * e))
      if (k < 1) raf = requestAnimationFrame(tick)
      else from.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target])
  return v
}

const SPARKS = Array.from({ length: 22 }, (_, i) => {
  const a = (i / 22) * Math.PI * 2
  const r = 46 + (i % 4) * 16
  return { dx: Math.cos(a) * r, dy: Math.sin(a) * r - 24, c: ['#16a36a', '#38a9ef', '#f59e0b', '#8b5cf6', '#ef4444'][i % 5], d: (i % 5) * 40 }
})

export function TaskBoard() {
  const [tasks, setTasks] = useState(INITIAL)
  const [cursor, setCursor] = useState({ x: 40, y: 40, show: false, grab: false, who: 'A' })
  const [lift, setLift] = useState(null)
  const [typing, setTyping] = useState(null)
  const [bubble, setBubble] = useState(null)
  const [toasts, setToasts] = useState([])
  const [feed, setFeed] = useState([
    { id: 'f0', who: 'S', text: 'updated Payroll integration', ago: '2m' },
    { id: 'f1', who: 'K', text: 'closed QA checklist', ago: '9m' },
  ])
  const [bell, setBell] = useState(0)
  const [pulse, setPulse] = useState(null)
  const [burst, setBurst] = useState(0)
  const [fade, setFade] = useState(false)
  const [inView, setInView] = useState(false)

  const root = useRef(null)
  const board = useRef(null)
  const cards = useRef({})
  const before = useRef(null)

  // Work that lands mid-sprint joins the backlog, so it stays out of the sprint figure.
  const sprint = tasks.filter((x) => x.id !== NEW_TASK.id)
  const total = sprint.length
  const avg = Math.round(sprint.reduce((n, x) => n + (x.col === 'done' ? 100 : x.p), 0) / total)
  const shown = useCount(avg)
  const doneCount = sprint.filter((x) => x.col === 'done').length

  // Pause the script whenever the board is off screen.
  useEffect(() => {
    const el = root.current
    if (!el) return undefined
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // FLIP: after a move, glide every card from its old spot to its new one, lifting the dragged card.
  useLayoutEffect(() => {
    const prev = before.current
    if (!prev) return
    before.current = null
    Object.entries(cards.current).forEach(([id, el]) => {
      const a = prev[id]
      if (!el || !a || !el.animate) return
      const b = el.getBoundingClientRect()
      const dx = a.left - b.left
      const dy = a.top - b.top
      if (!dx && !dy) return
      const moved = id === prev.__moved
      el.animate(
        moved
          ? [
              { transform: `translate(${dx}px, ${dy}px) rotate(0deg) scale(1)`, zIndex: 8 },
              { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 14}px) rotate(-3.5deg) scale(1.05)`, zIndex: 8, offset: 0.45 },
              { transform: 'translate(0, 0) rotate(0.8deg) scale(1.01)', zIndex: 8, offset: 0.85 },
              { transform: 'none', zIndex: 8 },
            ]
          : [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
        { duration: moved ? 1000 : 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      )
    })
  }, [tasks])

  useEffect(() => {
    if (!inView || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    let dead = false
    const timers = []
    const w = (ms) =>
      new Promise((resolve, reject) => {
        timers.push(
          setTimeout(() => {
            if (dead) reject(new Error('stop'))
            else resolve()
          }, ms),
        )
      })

    const posOf = (id, fx = 0.55) => {
      const el = cards.current[id]
      const bb = board.current?.getBoundingClientRect()
      if (!el || !bb) return null
      const r = el.getBoundingClientRect()
      return { x: r.left - bb.left + r.width * fx, y: r.top - bb.top + r.height * 0.45 }
    }
    const toast = (icon, text) => {
      const id = `${Date.now()}${Math.random()}`
      setToasts((list) => [...list.slice(-1), { id, icon, text }])
      timers.push(setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 2900))
    }
    const log = (who, text) => {
      setFeed((list) => [{ id: `${Date.now()}${Math.random()}`, who, text, ago: 'now' }, ...list.map((x) => (x.ago === 'now' ? { ...x, ago: '1m' } : x))].slice(0, 4))
      setPulse(who)
      timers.push(setTimeout(() => setPulse(null), 1400))
    }
    const patch = (id, fn) => setTasks((list) => list.map((x) => (x.id === id ? { ...x, ...fn(x) } : x)))

    // Drags a card to another stage with the cursor riding along.
    const drag = async (id, col, who, label, colName) => {
      const p = posOf(id)
      if (p) setCursor({ ...p, show: true, grab: false, who })
      await w(1000)
      setCursor((c) => ({ ...c, grab: true }))
      setLift(id)
      await w(380)
      const snap = {}
      Object.entries(cards.current).forEach(([k, el]) => {
        if (el) snap[k] = el.getBoundingClientRect()
      })
      snap.__moved = id
      before.current = snap
      setLift(null)
      setTasks((list) => list.map((x) => (x.id === id ? { ...x, col, fresh: false } : x)))
      await w(40)
      const q = posOf(id)
      if (q) setCursor((c) => ({ ...c, x: q.x, y: q.y }))
      await w(620)
      setCursor((c) => ({ ...c, grab: false }))
      toast('move', `${PEOPLE[who][0]} moved ${label} to ${colName}`)
      log(who, `moved ${label} to ${colName}`)
      await w(500)
    }

    const episode = async () => {
      setFade(false)
      setTasks(INITIAL)
      setBell(0)
      setBurst(0)
      setCursor((c) => ({ ...c, show: false, grab: false }))
      await w(1400)

      // 1. Aarav starts the homepage design.
      await drag('t1', 'prog', 'A', 'Design homepage', 'In progress')
      await w(500)

      // 2. Payroll integration fills up, then goes to review.
      patch('t3', () => ({ p: 100, sub: '5/5' }))
      toast('check', 'Payroll integration: 5 of 5 subtasks done')
      await w(1700)
      await drag('t3', 'review', 'S', 'Payroll integration', 'In review')
      setCursor((c) => ({ ...c, show: false }))
      await w(700)

      // 3. New work lands in To do and pings the assignee.
      setTasks((list) => [...list, NEW_TASK])
      setBell(1)
      toast('plus', 'Kavya was assigned Client review')
      log('K', 'was assigned Client review')
      await w(2300)

      // 4. A reviewer comments on the onboarding flow.
      setTyping('t5')
      await w(1500)
      setTyping(null)
      patch('t5', (x) => ({ cm: x.cm + 1 }))
      setBubble({ id: 't5', text: 'Looks great, ship it!' })
      log('R', 'commented on Onboarding flow')
      await w(2300)
      setBubble(null)

      // 5. Onboarding flow is approved and closes with a flourish.
      await drag('t5', 'done', 'M', 'Onboarding flow', 'Done')
      patch('t5', () => ({ p: 100, due: 'Done', tone: 'ok', sub: '9/9' }))
      setBurst(Date.now())
      setCursor((c) => ({ ...c, show: false }))
      await w(2200)

      // 6. A reminder goes out for the task due soon.
      patch('t2', () => ({ tone: 'warn', due: 'Tomorrow' }))
      toast('bell', 'Reminder sent: Write release notes is due tomorrow')
      log('R', 'got a due-date reminder')
      await w(3200)

      setFade(true)
      await w(700)
    }

    ;(async () => {
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) await episode()
      } catch {
        /* stopped */
      }
    })()

    return () => {
      dead = true
      timers.forEach(clearTimeout)
    }
  }, [inView])

  const counts = useMemo(() => Object.fromEntries(COLS.map(([id]) => [id, tasks.filter((x) => x.col === id).length])), [tasks])
  const R = 15
  const C = 2 * Math.PI * R

  return (
    <div className={t.root} ref={root} data-rv aria-hidden="true">
      <div className={`${t.board} ${fade ? t.fade : ''}`} ref={board}>
        <div className={t.main}>
          <div className={t.top}>
            <span className={t.dots}>
              <i /> <i /> <i />
            </span>
            <div className={t.crumb}>
              <b>Website launch</b>
              <span>Sprint 24</span>
            </div>
            <span className={t.live}>
              <i /> Live
            </span>
            <div className={t.topRight}>
              <div className={t.team}>
                {Object.keys(PEOPLE).map((k) => (
                  <span key={k} className={t.presence}>
                    <Av k={k} size={26} pulse={pulse === k} />
                  </span>
                ))}
              </div>
              <span className={t.bell}>
                <Ic n="bell" size={16} />
                {bell > 0 && <em key={bell}>{bell}</em>}
              </span>
            </div>
          </div>

          <div className={t.views}>
            <span className={t.viewOn}>Kanban</span>
            <span>Calendar</span>
            <span>Gantt</span>
            <div className={t.sprint}>
              <svg viewBox="0 0 36 36" aria-hidden="true">
                <circle cx="18" cy="18" r={R} className={t.ringBg} />
                <circle cx="18" cy="18" r={R} className={t.ring} style={{ strokeDasharray: C, strokeDashoffset: C * (1 - avg / 100) }} />
              </svg>
              <div>
                <b>{shown}%</b>
                <small>
                  Sprint · {doneCount}/{total} done
                </small>
              </div>
            </div>
          </div>

          <div className={t.cols}>
            {COLS.map(([id, name, c]) => (
              <div key={id} className={t.col}>
                <div className={t.colHead}>
                  <i style={{ background: c }} />
                  {name}
                  <em key={counts[id]}>{counts[id]}</em>
                </div>
                <div className={t.cards}>
                  {tasks
                    .filter((x) => x.col === id)
                    .map((x) => (
                      <div
                        key={x.id}
                        ref={(el) => {
                          cards.current[x.id] = el
                        }}
                        className={`${t.card} ${lift === x.id ? t.lifted : ''} ${x.fresh ? t.fresh : ''}`}
                      >
                        <span className={t.tag} style={{ background: x.tag }} />
                        {x.fresh && <span className={t.new}>New</span>}
                        {x.col === 'done' && (
                          <span className={t.doneMark}>
                            <Ic n="check" size={12} />
                          </span>
                        )}
                        <b className={t.title}>{x.title}</b>
                        <div className={t.meta}>
                          <span className={`${t.due} ${x.tone ? t[x.tone] : ''}`}>
                            <Ic n="clock" size={11} /> {x.due}
                          </span>
                          <span>
                            <Ic n="list" size={11} /> {x.sub}
                          </span>
                          <span>
                            <Ic n="comment" size={11} /> {x.cm}
                          </span>
                          {x.att > 0 && (
                            <span>
                              <Ic n="clip" size={11} /> {x.att}
                            </span>
                          )}
                        </div>
                        <div className={t.foot}>
                          <Av k={x.who} />
                          <span className={t.pbar}>
                            <i style={{ width: `${x.col === 'done' ? 100 : x.p}%`, background: x.col === 'done' ? '#16a36a' : undefined }} />
                          </span>
                          <small>{x.col === 'done' ? 100 : x.p}%</small>
                        </div>
                        {typing === x.id && (
                          <span className={t.typing}>
                            <Av k="R" size={18} />
                            <i /> <i /> <i />
                          </span>
                        )}
                        {bubble?.id === x.id && (
                          <span className={t.bubble}>
                            <Av k="R" size={18} />
                            {bubble.text}
                          </span>
                        )}
                      </div>
                    ))}
                  {id === 'done' && burst > 0 && (
                    <span key={burst} className={t.sparks}>
                      {SPARKS.map((p, i) => (
                        <i key={i} style={{ '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, background: p.c, animationDelay: `${p.d}ms` }} />
                      ))}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={t.toasts}>
            {toasts.map((x) => (
              <div key={x.id} className={t.toast}>
                <b>
                  <Ic n={x.icon} size={13} />
                </b>
                {x.text}
              </div>
            ))}
          </div>

          <div className={`${t.cursor} ${cursor.show ? t.cursorOn : ''} ${cursor.grab ? t.grab : ''}`} style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)` }}>
            <svg viewBox="0 0 20 24" width="20" height="24">
              <path d="M2 2l15 8-6.5 2L8 19z" fill={PEOPLE[cursor.who][1]} stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
            <span style={{ background: PEOPLE[cursor.who][1] }}>{PEOPLE[cursor.who][0]}</span>
          </div>
        </div>

        <aside className={t.side}>
          <div className={t.sideHead}>
            <b>Activity</b>
            <span>
              <i /> Live
            </span>
          </div>
          <ul className={t.feed}>
            {feed.map((f) => (
              <li key={f.id}>
                <Av k={f.who} size={26} />
                <p>
                  <b>{PEOPLE[f.who][0]}</b> {f.text}
                </p>
                <small>{f.ago}</small>
              </li>
            ))}
          </ul>
          <div className={t.sideFoot}>
            <b>Due this week</b>
            <div className={t.week}>
              {['M', 'T', 'W', 'T', 'F'].map((d, i) => (
                <span key={i} className={i === 4 ? t.weekDue : undefined}>
                  <small>{d}</small>
                  <i style={{ height: [18, 30, 22, 14, 36][i] }} />
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
